import { BlobServiceClient, BlockBlobClient } from '@azure/storage-blob'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'

import { EuiButton, EuiCallOut, EuiFilePicker, EuiFlexGroup, EuiFlexItem, EuiLoadingContent, EuiSpacer, EuiText } from '@elastic/eui'

import { apiGetAudioBuilderUpload, apiPostAudioAd } from 'api/adcritterApi'
import { showErrorToast, showSuccessToast } from 'app/appSlice'
import { RootState } from 'app/rootReducer'
import history from 'services/HistoryService'
import { readAudioDurationSeconds } from 'utils/audioDuration'

import { fetchCampaign } from '../../campaign/campaignSlice'
import { resetCreateAd, setUploadedUrl } from './retail/createAdAudioRetailSlice'

const ACCEPTED_EXTENSIONS = ['.mp3', '.mp4', '.m4a', '.wav']
const ACCEPTED_MIME_TYPES = 'audio/mpeg,audio/mp4,audio/wav,audio/x-m4a,audio/x-wav,.mp3,.mp4,.m4a,.wav'
const MAX_FILE_SIZE_BYTES = 250 * 1024 * 1024
const MAX_DURATION_SECONDS = 120

interface AudioUploaderProps {
  onBackClick?: () => void
  showBackButton?: boolean
}

const getExtension = (fileName: string): string => {
  const dotIndex = fileName.lastIndexOf('.')
  return dotIndex >= 0 ? fileName.slice(dotIndex).toLowerCase() : ''
}

const AudioUploader: React.FC<AudioUploaderProps> = ({ onBackClick, showBackButton = true }) => {
  const dispatch = useDispatch()
  const { currentAccount } = useSelector((state: RootState) => state.app)
  const campaign = useSelector((state: RootState) => state.campaign)
  const [destination, setDestination] = useState<string | null>(null)
  const [containerName, setContainerName] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasFile, setHasFile] = useState(false)
  const [uploadedAudioUrl, setUploadedAudioUrl] = useState<string>('')
  const [previewUrl, setPreviewUrl] = useState<string>('')
  const fileSelectionSequence = useRef(0)

  useEffect(() => {
    if (currentAccount) {
      apiGetAudioBuilderUpload(currentAccount.id).then(data => {
        setDestination(data.data)
        setContainerName(currentAccount.id)
      })
    }
  }, [currentAccount])

  const onFileChange = async (files: FileList | null) => {
    const selectionSequence = ++fileSelectionSequence.current
    if (!files || files.length === 0) {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
      setPreviewUrl('')
      setUploadedAudioUrl('')
      setHasFile(false)
      return
    }

    const file = files[0]
    const extension = getExtension(file.name)

    if (!ACCEPTED_EXTENSIONS.includes(extension)) {
      dispatch(showErrorToast('Unsupported streaming audio format. Supported formats: MP3, MP4, M4A, WAV.'))
      return
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      dispatch(showErrorToast('Streaming audio file exceeds the 250 MB size limit.'))
      return
    }

    if (!destination || !containerName) {
      return
    }

    const durationSeconds = await readAudioDurationSeconds(file)
    if (selectionSequence !== fileSelectionSequence.current) {
      return
    }

    if (durationSeconds !== null && durationSeconds > MAX_DURATION_SECONDS) {
      dispatch(showErrorToast(`Streaming audio file exceeds the ${MAX_DURATION_SECONDS} second length limit.`))
      return
    }

    const candidatePreviewUrl = URL.createObjectURL(file)

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
    }

    const storageClient = new BlobServiceClient(destination)
    const containerClient = storageClient.getContainerClient(containerName)
    const blockBlobClient: BlockBlobClient = containerClient.getBlockBlobClient(uuidv4() + extension)

    setPreviewUrl(candidatePreviewUrl)
    setIsUploading(true)

    blockBlobClient
      .uploadData(file, {
        blockSize: 4 * 1024 * 1024,
        concurrency: 20
      })
      .then(() => {
        const pathUrl = blockBlobClient.url.split('?')[0]
        setUploadedAudioUrl(pathUrl)
        dispatch(setUploadedUrl(pathUrl))
        setHasFile(true)
      })
      .catch(() => {
        dispatch(showErrorToast('Failed to upload streaming audio file. Please try again.'))
      })
      .finally(() => {
        setIsUploading(false)
      })
  }

  const createAd = () => {
    setIsSubmitting(true)
    apiPostAudioAd(currentAccount!.id, {
      name: `Uploaded Streaming Audio Ad ${campaign.ads.length + 1}`,
      uploadedUrl: uploadedAudioUrl,
      campaignIds: [campaign.campaign!.id!]
    })
      .then(() => {
        dispatch(showSuccessToast('Created new streaming audio ad'))
        dispatch(resetCreateAd())
        dispatch(fetchCampaign(currentAccount!.id, campaign.campaign!.id))
        history.push(`/campaigns/edit/${campaign.campaign!.id}/details`)
      })
      .catch(() => {
        dispatch(showErrorToast('Failed to create streaming audio ad. Please try again.'))
        setIsSubmitting(false)
      })
  }

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick()
    } else {
      history.goBack()
    }
  }

  if (!destination) {
    return <EuiLoadingContent />
  }

  return (
    <>
      <EuiFlexGroup>
        <EuiFlexItem style={{ marginLeft: 25 }}>
          <EuiCallOut title='Start by uploading your streaming audio ad, making sure it meets all requirements.' color='primary' iconType='importAction'>
            <EuiText size='s'>
              <p>Upload a 15, 30, or 60 second streaming audio ad.</p>
            </EuiText>
          </EuiCallOut>
          <EuiSpacer size='xl' />

          <EuiFilePicker id='upload' accept={ACCEPTED_MIME_TYPES} fullWidth display='large' multiple={false} initialPromptText='Select or drag and drop streaming audio file here' onChange={onFileChange} isLoading={isUploading} />
          <EuiSpacer />
          <EuiCallOut title='Requirements:' color='primary' iconType='importAction'>
            <EuiSpacer />
            <EuiText size='s'>
              <p style={{ marginLeft: 30, marginTop: -20 }}>
                <strong>Time:</strong> 15, 30, or 60 seconds. (Exactly.)
                <br />
                <strong>Formats:</strong> MP3, MP4, M4A, WAV
                <br />
                <strong>Max Size:</strong> 250 MB
              </p>
            </EuiText>
          </EuiCallOut>

          <EuiSpacer size='xl' />
        </EuiFlexItem>

        <EuiFlexItem style={{ paddingLeft: 50, paddingRight: 50 }}>
          <EuiText size='s'>
            <h4>Preview Your Streaming Audio Ad</h4>
          </EuiText>
          <EuiSpacer size='s' />
          <EuiFlexItem>
            <audio id='uploadedAudio' controls preload='metadata' src={previewUrl} style={{ width: '100%' }} />
          </EuiFlexItem>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiFlexGroup>
        {showBackButton && (
          <EuiFlexItem grow={false}>
            <EuiButton id='back' fill type='button' color='text' onClick={handleBackClick} iconType='arrowLeft' iconSide='left' isDisabled={isSubmitting}>
              Back
            </EuiButton>
          </EuiFlexItem>
        )}
        <EuiFlexItem grow={false}>
          <EuiButton id='continue' fill type='submit' isLoading={isSubmitting} isDisabled={!hasFile || isUploading || isSubmitting} iconType='arrowRight' iconSide='right' onClick={createAd}>
            Submit Streaming Audio Ad
          </EuiButton>
        </EuiFlexItem>
      </EuiFlexGroup>
    </>
  )
}

export default AudioUploader
